import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import classes from "./artistPhotoCarousel.module.css";

const ArtistPhotoCarousel = ({ photos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  if (!photos || photos.length === 0) {
    return (
      <div className={classes.emptyContainer}>
        <div className={classes.emptyIcon}>📷</div>
        <p>No photos available</p>
      </div>
    );
  }

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === photos.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? photos.length - 1 : prevIndex - 1
    );
  };

  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerVisible(true);
    document.body.style.overflow = "hidden";
  };

  const closeViewer = () => {
    setViewerVisible(false);
    document.body.style.overflow = "auto";
  };

  const nextViewerImage = (e) => {
    e.stopPropagation();
    setViewerIndex((prevIndex) =>
      prevIndex === photos?.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevViewerImage = (e) => {
    e.stopPropagation();
    setViewerIndex((prevIndex) =>
      prevIndex === 0 ? photos?.length - 1 : prevIndex - 1
    );
  };

  return (
    <div className={classes.carouselContainer}>
      <h2 className={classes.sectionTitle}>Photos</h2>

      {/* Main Carousel */}
      <div className={classes.carouselWrapper}>
        <button
          className={`${classes.carouselArrow} ${classes.left}`}
          onClick={prevSlide}
          aria-label="Previous photo"
        >
          &#10094;
        </button>

        <div className={classes.carousel}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className={classes.slide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => openViewer(currentIndex)}
            >
              <div className={classes.imageContainer}>
                <img
                  src={photos[currentIndex]?.mediaLink || photos[currentIndex]}
                  alt={`Artist photo ${currentIndex + 1}`}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <button
          className={`${classes.carouselArrow} ${classes.right}`}
          onClick={nextSlide}
          aria-label="Next photo"
        >
          &#10095;
        </button>
      </div>

      {/* Thumbnails */}
      <div className={classes.thumbnails}>
        {photos.map((photo, index) => (
          <div
            key={index}
            className={`${classes.thumbnail} ${
              index === currentIndex ? classes.activeThumbnail : ""
            }`}
            onClick={() => setCurrentIndex(index)}
          >
            <div className={classes.thumbnailContainer}>
              <img
                src={photo?.mediaLink || photo}
                alt={`Thumbnail ${index + 1}`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Full Screen Viewer */}
      {viewerVisible && (
        <div className={classes.viewerOverlay} onClick={closeViewer}>
          <button
            className={classes.closeViewer}
            onClick={closeViewer}
            aria-label="Close viewer"
          >
            ×
          </button>

          <div
            className={classes.viewerContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`${classes.viewerArrow} ${classes.viewerLeft}`}
              onClick={prevViewerImage}
              aria-label="Previous full-size photo"
            >
              &#10094;
            </button>

            <div className={classes.viewerImageContainer}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={viewerIndex}
                  src={photos[viewerIndex]?.mediaLink || photos[viewerIndex]}
                  alt={`Full size photo ${viewerIndex + 1}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </AnimatePresence>
            </div>

            <button
              className={`${classes.viewerArrow} ${classes.viewerRight}`}
              onClick={nextViewerImage}
              aria-label="Next full-size photo"
            >
              &#10095;
            </button>
          </div>

          <div className={classes.viewerCounter}>
            {viewerIndex + 1} / {photos?.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistPhotoCarousel;
